import { BaseDto } from 'stores/types';
import { AbstractValidator } from 'fluent-ts-validator';
import i18n from 'i18n';
import { repository } from 'redux-scaffolding-ts';
import { DataStore } from 'stores/dataStore';
import { FormStore } from 'stores/formStore';
import HttpService from 'services/http-service';
import { container } from 'inversify.config';
import { ItemResult } from 'stores/types';
import * as FileSaver from 'file-saver';

//const checklistBaseUrl = 'http://localhost:7071/api/v1';
const checklistBaseUrl = 'events/v1';

export enum CheckListRoles {
  Planner = 10,
  PoC = 20,
  Instructor = 30,
  Employee = 40,
  PlannerMTC = 50
}

export interface CheckListTemplateFileDto {
  originalName: string;
  uploadedName: string;
}

export interface ChecklistTemplateUploadDto {
  uploadedFileName: string;
  temporaryId: string;
}

export const CHECKLIST_FILE_MAX_SIZE = 5 * 1024 * 1024;
export const CHECKLIST_FILE_ALLOWED_EXTENSIONS = ['xlsx', 'xls'];

export function isValidChecklistExcelFile(file: File): boolean {
  const extension = file.name
    .split('.')
    .pop()
    ?.toLowerCase();
  return file.size > 0 && file.size <= CHECKLIST_FILE_MAX_SIZE && CHECKLIST_FILE_ALLOWED_EXTENSIONS.includes(extension || '');
}

export async function uploadChecklistTemplate(file: File, checklistId?: string, eventTypeName?: string) {
  const formData = new FormData();
  formData.append('file', file);
  const query = new URLSearchParams();
  if (checklistId) query.append('checklistId', checklistId);
  if (eventTypeName) query.append('eventTypeName', eventTypeName);
  const queryString = query.toString() ? `?${query.toString()}` : '';
  const httpService = container.get(HttpService);
  return httpService.postMultipart<ItemResult<ChecklistTemplateUploadDto>>(
    `${checklistBaseUrl}/upload-checklist-template${queryString}`,
    formData
  );
}

export async function uploadEventTypeChecklistTemplate(file: File, eventTypeName: string, role: string, userName: string) {
  const formData = new FormData();
  formData.append('file', file);
  const query = new URLSearchParams({ role, userName, eventTypeName });
  const httpService = container.get(HttpService);
  return httpService.postMultipart<ItemResult<ChecklistTemplateUploadDto>>(
    `${checklistBaseUrl}/upload-checklist-template?${query.toString()}`,
    formData
  );
}

export async function downloadChecklistFile(uploadedName: string, isTemplateFile: boolean = true) {
  const httpService = container.get(HttpService);
  return httpService.get<ItemResult<string>>(`${checklistBaseUrl}/download-checklist-file`, {
    uploadedName,
    isTemplateFile
  });
}

export async function downloadChecklistFileAs(fileName: string, uploadedName: string, isTemplateFile: boolean = true) {
  const result = await downloadChecklistFile(uploadedName, isTemplateFile);
  if (!result.data.isSuccess || !result.data.item) {
    return result;
  }

  const response = await fetch(result.data.item);
  if (!response.ok) {
    throw new Error('File download failed');
  }

  const blob = await response.blob();
  FileSaver.saveAs(blob, fileName);
  return result;
}

export interface CheckListDto extends BaseDto {
  id: string;
  description: string;
  eventTypeId: string;
  eventTypeName: string;
  rolesThatCreateCheckLists: string[];
  isCheckListNew: boolean;
}

export interface CreateCheckListDto {
  description: string;
  eventTypeId: string;
  rolesThatCreateCheckLists: string[];
  isCheckListNew: boolean;
}

export interface ChangeCheckListDto {
  id: string;
  description: string;
  eventTypeId: string;
  rolesThatCreateCheckLists: string[];
  isCheckListNew: boolean;
}

export class CreateCheckListValidator extends AbstractValidator<CreateCheckListDto> {
  private roles = ['Planner', 'PoC', 'Instructor', 'Employee', 'PlannerMTC'];

  constructor() {
    super();

    this.validateIfString(o => o.description)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Description is required')));

    this.validateIfString(o => o.eventTypeId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Event Type Id is required')));

    this.validateIf(o => o.rolesThatCreateCheckLists)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('CheckList creator role is required')));

    this.validateIfEach(o => o.rolesThatCreateCheckLists)
      .isIn(this.roles)
      .whenDefined()
      .whenNotNull()
      .withFailureMessage(String(i18n.t('CheckList creator role is invalid')));
  }
}

export class ChangeCheckListValidator extends AbstractValidator<ChangeCheckListDto> {
  private roles = ['Planner', 'PoC', 'Instructor', 'Employee', 'PlannerMTC'];

  constructor() {
    super();

    this.validateIfString(o => o.id)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Id is required')));

    this.validateIfString(o => o.eventTypeId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Event Type Id is required')));

    this.validateIfString(o => o.description)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Description is required')));

    this.validateIf(o => o.rolesThatCreateCheckLists)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('CheckList creator role is required')));

    this.validateIfEach(o => o.rolesThatCreateCheckLists)
      .isIn(this.roles)
      .whenDefined()
      .whenNotNull()
      .withFailureMessage(String(i18n.t('CheckList creator role is invalid')));
  }
}

@repository('@@CHECKLISTS', 'checklists.summary')
export class CheckListStore extends DataStore<CheckListDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = 'create-checklist';
  retrievePath = 'get-checklists';
  updatePath = 'update-checklist';
  deletePath = 'delete-checklist';

  protected validate(item: CheckListDto) {
    return new ChangeCheckListValidator().validate(item);
  }

  constructor() {
    super('CHECKLIST', {
      isBusy: false,
      items: [],
      count: 0,
      result: undefined,
      discard: item => {}
    });
  }
}

@repository('@@CHECKLISTS', 'checklists.new')
export class NewCheckListStore extends FormStore<CreateCheckListDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = 'create-checklist';
  retrievePath = 'get-checklist';
  updatePath = 'update-checklist';

  protected validate(item: CreateCheckListDto) {
    return new CreateCheckListValidator().validate(item);
  }

  constructor() {
    super('NEW_CHECKLIST', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    });
  }
}

@repository('@@CHECKLISTS', 'checklists.change')
export class ChangeCheckListStore extends FormStore<ChangeCheckListDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = 'create-checklist';
  retrievePath = 'get-checklist';
  updatePath = 'update-checklist';

  protected validate(item: ChangeCheckListDto) {
    return new ChangeCheckListValidator().validate(item);
  }

  constructor() {
    super('CHANGE_CHECKLIST', {
      isBusy: false,
      status: 'Modified',
      item: undefined,
      result: undefined
    });
  }
}
